import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatDate, formatINRFull, formatStatus, getStatusColor } from '@/lib/utils';
import { Plus, Shield, Calendar, AlertTriangle, Check, ArrowLeft, Clock, Download, Edit2, Trash2 } from 'lucide-react';

const AMC_STATUSES = ['ACTIVE', 'EXPIRED', 'CANCELLED'];
const SYSTEM_TYPES = ['CCTV', 'FIRE_ALARM', 'ACCESS_CONTROL', 'PA_SYSTEM', 'AUTOMATION', 'MIXED'];
const PM_FREQUENCIES = ['MONTHLY', 'QUARTERLY', 'HALF_YEARLY', 'ANNUALLY'];

export default function AMCPage() {
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [summary, setSummary] = useState({});
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  const fetchContracts = () => {
    const params = new URLSearchParams();
    if (filterStatus) params.set('status', filterStatus);
    api.get(`/amc?${params}`).then(r => setContracts(r.data.contracts || [])).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchContracts(); api.get('/amc/dashboard/summary').then(r => setSummary(r.data)).catch(() => {}); }, []);
  useEffect(() => { fetchContracts(); }, [filterStatus]);
  useEffect(() => { api.get('/clients').then(r => setClients(r.data.clients || [])); }, []);

  const canCreate = ['DIRECTOR', 'HR_ADMIN'].includes(user?.role);

  const deleteContract = async (contractId, contractCode) => {
    if (!window.confirm(`Delete AMC ${contractCode}? This will move it to Deleted Items.`)) return;
    try {
      await api.delete(`/amc/${contractId}`);
      fetchContracts();
    } catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  const viewContract = async (cId) => {
    try {
      const res = await api.get(`/amc/${cId}`);
      setSelectedContract(res.data.contract);
    } catch (err) { alert('Failed'); }
  };

  if (selectedContract) {
    return <ContractDetail contract={selectedContract} onBack={() => setSelectedContract(null)} onRefresh={() => viewContract(selectedContract.id)} />;
  }

  return (
    <div data-testid="amc-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">AMC Contracts</h1>
          <p className="text-sm text-slate-500">{contracts.length} contracts</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowCreate(true)} data-testid="add-amc-btn"
            className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-2">
            <Plus size={16} /> New Contract
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
          <div className="text-xl font-bold text-[#1B5486]">{summary.active_contracts || 0}</div>
          <div className="text-xs text-slate-500">Active Contracts</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
          <div className="text-xl font-bold text-green-600">{formatINRFull(summary.total_value || 0)}</div>
          <div className="text-xs text-slate-500">Total Value</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
          <div className="text-xl font-bold text-amber-600">{summary.upcoming_visits || 0}</div>
          <div className="text-xs text-slate-500">Upcoming Visits</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
          <div className="text-xl font-bold text-red-600">{summary.overdue_visits || 0}</div>
          <div className="text-xs text-slate-500">Overdue Visits</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="amc-filter-status">
          <option value="">All Statuses</option>
          {AMC_STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}
        </select>
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {contracts.map(c => (
            <div key={c.id} onClick={() => viewContract(c.id)} data-testid={`amc-card-${c.id}`}
              className="bg-white rounded-lg border border-slate-200 p-4 hover:border-[#1B5486]/30 hover:shadow-sm cursor-pointer transition-all relative group">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-xs font-mono text-[#1B5486] font-medium">{c.contract_code}</p>
                  <h3 className="text-sm font-semibold text-slate-900">{c.client_name}</h3>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${c.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : c.status === 'EXPIRED' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{c.status}</span>
              </div>
              <p className="text-xs text-slate-500 mb-2">{c.site_name} | {formatStatus(c.system_type)}</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">{formatDate(c.start_date)} - {formatDate(c.end_date)}</span>
                <span className="font-semibold text-slate-900">{formatINRFull(c.contract_value)}</span>
              </div>
              {c.visit_counter && (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <span className="text-green-600">PM: {c.visit_counter.pm_visits_completed || 0}/{(c.visit_counter.pm_visits_completed || 0) + (c.visit_counter.pm_visits_remaining || 0)}</span>
                  <span className="text-slate-300">|</span>
                  <span className="text-slate-500">Compliance: {c.visit_counter.compliance_percent || 0}%</span>
                </div>
              )}
              {user?.role === 'DIRECTOR' && (
                <button onClick={e => { e.stopPropagation(); deleteContract(c.id, c.contract_code); }} data-testid={`delete-amc-${c.id}`}
                  className="absolute top-3 right-3 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
              )}
            </div>
          ))}
          {contracts.length === 0 && <div className="col-span-3 text-center py-12 text-sm text-slate-400">No contracts found</div>}
        </div>
      )}

      {showCreate && <CreateContractModal clients={clients} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchContracts(); }} />}
    </div>
  );
}

function ContractDetail({ contract, onBack, onRefresh }) {
  const { user } = useAuth();
  const [completingVisit, setCompletingVisit] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const canComplete = ['DIRECTOR', 'SUPERVISOR', 'HR_ADMIN'].includes(user?.role);
  const canEdit = ['DIRECTOR', 'SALES', 'HR_ADMIN'].includes(user?.role);

  return (
    <div data-testid="amc-detail" className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-slate-400 hover:text-slate-600"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-slate-900 font-heading">{contract.contract_code}</h1>
          <p className="text-sm text-slate-500">{contract.client?.name} | {contract.site_name}</p>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full ${contract.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{contract.status}</span>
        {canEdit && <button onClick={() => setEditMode(true)} data-testid="edit-amc-btn" className="text-xs px-3 py-1.5 border border-slate-300 text-slate-600 rounded-md hover:bg-slate-50 flex items-center gap-1"><Edit2 size={14} /> Edit</button>}
        <button onClick={async () => {
          try {
            const r = await api.get(`/amc/${contract.id}/visit-report-pdf`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `AMC_Report_${contract.contract_code}.pdf`;
            a.click();
          } catch (err) { alert('Failed to generate report'); }
        }} data-testid="download-amc-report" className="text-xs px-3 py-1.5 border border-[#1B5486] text-[#1B5486] rounded-md hover:bg-[#1B5486]/5 flex items-center gap-1">
          <Download size={14} /> Report PDF
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Contract Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">System Type</span><span>{formatStatus(contract.system_type)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Value</span><span className="font-bold">{formatINRFull(contract.contract_value)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Period</span><span>{formatDate(contract.start_date)} - {formatDate(contract.end_date)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">PM Frequency</span><span>{formatStatus(contract.pm_frequency)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">PM Visits/Year</span><span>{contract.pm_visits_per_year}</span></div>
            {contract.assigned_engineer_name && <div className="flex justify-between"><span className="text-slate-500">Assigned Engineer</span><span className="font-medium text-[#1B5486]">{contract.assigned_engineer_name}</span></div>}
            {contract.system_description && <div className="pt-2 text-xs text-slate-500">{contract.system_description}</div>}
          </div>
        </div>

        {contract.visit_counter && (
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Visit Tracker</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">PM Completed</span><span className="text-green-600 font-medium">{contract.visit_counter.pm_visits_completed}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">PM Remaining</span><span className="text-amber-600">{contract.visit_counter.pm_visits_remaining}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Breakdowns Used</span><span>{contract.visit_counter.breakdown_visits_used || 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Compliance</span><span className="font-bold">{contract.visit_counter.compliance_percent}%</span></div>
              {contract.visit_counter.last_visit_date && <div className="flex justify-between"><span className="text-slate-500">Last Visit</span><span>{formatDate(contract.visit_counter.last_visit_date)}</span></div>}
            </div>
          </div>
        )}
      </div>

      {contract.visits?.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Scheduled Visits</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100">
                <th className="text-left py-2 text-xs text-slate-500">Date</th>
                <th className="text-left py-2 text-xs text-slate-500">Type</th>
                <th className="text-left py-2 text-xs text-slate-500">Status</th>
                <th className="text-left py-2 text-xs text-slate-500">Actual Date</th>
                <th className="w-20"></th>
              </tr></thead>
              <tbody>
                {contract.visits.map(v => (
                  <tr key={v.id} className="border-b border-slate-50">
                    <td className="py-2 text-slate-900">{formatDate(v.scheduled_date)}</td>
                    <td className="py-2"><span className={`text-xs px-1.5 py-0.5 rounded ${v.visit_type === 'PM' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{v.visit_type}</span></td>
                    <td className="py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${v.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : v.status === 'SCHEDULED' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>{v.status}</span></td>
                    <td className="py-2 text-slate-500">{v.actual_date ? formatDate(v.actual_date) : '-'}</td>
                    <td className="py-2">
                      {v.status === 'SCHEDULED' && canComplete && (
                        <button onClick={() => setCompletingVisit(v)} data-testid={`complete-visit-${v.id}`}
                          className="text-green-600 hover:text-green-800 text-xs flex items-center gap-1"><Check size={12} /> Complete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {completingVisit && (
        <CompleteVisitModal visit={completingVisit} contractId={contract.id}
          onClose={() => setCompletingVisit(null)}
          onCompleted={() => { setCompletingVisit(null); onRefresh(); }} />
      )}

      {editMode && (
        <EditContractModal contract={contract}
          onClose={() => setEditMode(false)}
          onSaved={() => { setEditMode(false); onRefresh(); }} />
      )}
    </div>
  );
}

function CompleteVisitModal({ visit, contractId, onClose, onCompleted }) {
  const [form, setForm] = useState({
    visit_date: visit.scheduled_date || new Date().toISOString().slice(0, 10),
    arrival_time: '', departure_time: '', work_summary: '',
    issues_found: '', parts_replaced: '', parts_cost: '0', client_contact_name: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/amc/visits/${visit.id}/complete`, form);
      onCompleted();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="complete-visit-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-1">Complete Visit</h2>
        <p className="text-xs text-slate-500 mb-4">{visit.visit_type} visit scheduled for {formatDate(visit.scheduled_date)}</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Visit Date *</label>
              <input type="date" value={form.visit_date} onChange={e => setForm({...form, visit_date: e.target.value})} required className="input-field w-full" data-testid="visit-date-input" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Arrival Time</label>
              <input type="time" value={form.arrival_time} onChange={e => setForm({...form, arrival_time: e.target.value})} className="input-field w-full" data-testid="visit-arrival-input" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Departure Time</label>
              <input type="time" value={form.departure_time} onChange={e => setForm({...form, departure_time: e.target.value})} className="input-field w-full" data-testid="visit-departure-input" /></div>
          </div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Work Summary *</label>
            <textarea value={form.work_summary} onChange={e => setForm({...form, work_summary: e.target.value})} required rows={3} placeholder="Describe the work done during this visit" className="input-field w-full" data-testid="visit-summary-input" /></div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Issues Found</label>
            <textarea value={form.issues_found} onChange={e => setForm({...form, issues_found: e.target.value})} rows={2} placeholder="Any issues or observations" className="input-field w-full" data-testid="visit-issues-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Parts Replaced</label>
              <input value={form.parts_replaced} onChange={e => setForm({...form, parts_replaced: e.target.value})} placeholder="e.g. 2x HDD, 1x Camera" className="input-field w-full" data-testid="visit-parts-input" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Parts Cost</label>
              <input type="number" value={form.parts_cost} onChange={e => setForm({...form, parts_cost: e.target.value})} className="input-field w-full" data-testid="visit-cost-input" /></div>
          </div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Client Contact Name</label>
            <input value={form.client_contact_name} onChange={e => setForm({...form, client_contact_name: e.target.value})} placeholder="Person who received the engineer" className="input-field w-full" data-testid="visit-contact-input" /></div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-visit-btn" className="flex-1 bg-emerald-600 text-white py-2 rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-60">
              {saving ? 'Saving...' : 'Mark Complete'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditContractModal({ contract, onClose, onSaved }) {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    site_name: contract.site_name || '', site_address: contract.site_address || '',
    system_type: contract.system_type || 'CCTV', system_description: contract.system_description || '',
    contract_value: contract.contract_value || '', status: contract.status || 'ACTIVE',
    start_date: contract.start_date || '', end_date: contract.end_date || '',
    pm_frequency: contract.pm_frequency || 'QUARTERLY', pm_visits_per_year: contract.pm_visits_per_year || '4',
    breakdown_visits_per_year: contract.breakdown_visits_per_year || '0',
    assigned_engineer_id: contract.assigned_engineer_id || '', notes: contract.notes || ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/employees').then(r => setEmployees(r.data.employees || [])); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.patch(`/amc/${contract.id}`, form); onSaved(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="edit-amc-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">Edit Contract</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Site Name *</label>
              <input value={form.site_name} onChange={e => setForm({...form, site_name: e.target.value})} required className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">System Type</label>
              <select value={form.system_type} onChange={e => setForm({...form, system_type: e.target.value})} className="input-field w-full">
                {SYSTEM_TYPES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}</select></div>
          </div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Site Address</label>
            <input value={form.site_address} onChange={e => setForm({...form, site_address: e.target.value})} className="input-field w-full" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Value</label>
              <input type="number" value={form.contract_value} onChange={e => setForm({...form, contract_value: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Start Date</label>
              <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">End Date</label>
              <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="input-field w-full">
                {AMC_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Assigned Engineer</label>
              <select value={form.assigned_engineer_id} onChange={e => setForm({...form, assigned_engineer_id: e.target.value})} className="input-field w-full" data-testid="edit-amc-engineer">
                <option value="">Select Engineer</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name} - {emp.designation || emp.employee_code}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">PM Frequency</label>
              <select value={form.pm_frequency} onChange={e => setForm({...form, pm_frequency: e.target.value})} className="input-field w-full">
                {PM_FREQUENCIES.map(f => <option key={f} value={f}>{formatStatus(f)}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">PM Visits/Yr</label>
              <input type="number" value={form.pm_visits_per_year} onChange={e => setForm({...form, pm_visits_per_year: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Breakdowns/Yr</label>
              <input type="number" value={form.breakdown_visits_per_year} onChange={e => setForm({...form, breakdown_visits_per_year: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="save-edit-amc-btn" className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60">
              {saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateContractModal({ clients, onClose, onCreated }) {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    client_id: '', site_name: '', site_address: '', system_type: 'CCTV', system_description: '',
    contract_value: '', start_date: '', end_date: '', pm_frequency: 'QUARTERLY',
    pm_visits_per_year: '4', breakdown_visits_per_year: '0', assigned_engineer_id: '', notes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/employees').then(r => setEmployees(r.data.employees || [])); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post('/amc', form); onCreated(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="create-amc-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">New AMC Contract</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Client *</label>
            <select value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} required className="input-field w-full">
              <option value="">Select Client</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Site Name *</label>
              <input value={form.site_name} onChange={e => setForm({...form, site_name: e.target.value})} required className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">System Type</label>
              <select value={form.system_type} onChange={e => setForm({...form, system_type: e.target.value})} className="input-field w-full">
                {SYSTEM_TYPES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}</select></div>
          </div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Site Address</label>
            <input value={form.site_address} onChange={e => setForm({...form, site_address: e.target.value})} className="input-field w-full" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Contract Value *</label>
              <input type="number" value={form.contract_value} onChange={e => setForm({...form, contract_value: e.target.value})} required className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Start Date *</label>
              <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} required className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">End Date *</label>
              <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} required className="input-field w-full" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">PM Frequency</label>
              <select value={form.pm_frequency} onChange={e => setForm({...form, pm_frequency: e.target.value})} className="input-field w-full">
                {PM_FREQUENCIES.map(f => <option key={f} value={f}>{formatStatus(f)}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">PM Visits/Year</label>
              <input type="number" value={form.pm_visits_per_year} onChange={e => setForm({...form, pm_visits_per_year: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Breakdown Visits</label>
              <input type="number" value={form.breakdown_visits_per_year} onChange={e => setForm({...form, breakdown_visits_per_year: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Assigned Engineer</label>
            <select value={form.assigned_engineer_id} onChange={e => setForm({...form, assigned_engineer_id: e.target.value})} className="input-field w-full" data-testid="amc-assigned-engineer">
              <option value="">Select Engineer</option>
              {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name} - {emp.designation || emp.employee_code}</option>)}
            </select></div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-amc-btn" className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60">
              {saving ? 'Creating...' : 'Create Contract'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
